import { useTranslation } from "react-i18next";
import {ScissorOutlined, DeleteOutlined, CloseOutlined, CheckOutlined} from "@ant-design/icons";
import {Select, Spin, Modal, Col, Form, Row, Space, Button, Input, DatePicker, InputNumber, Switch} from "antd";
import {useState, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {DoctorRequestDto} from "../api/client-axios";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {Problem} from "../model/problem";
import {deleteNoOrgDoctor, fetchNoOrgDoctors} from "../features/doctorsNoOrgSlice";
import {
    clearSaveNoOrgDoctorError,
    fetchNoOrgDoctor,
    initNoOrgDoctorState,
    saveNoOrgDoctor,
    updateNoOrgDoctorFields, updateNoOrgDoctorPhoneNumber
} from "../features/doctorNoOrgSlice";

const {Option} = Select;

export const EditNoOrgDoctorPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const doctorsStatus = useAppSelector((state: RootState) => state.doctorsNoOrg.status);
    const doctor = useAppSelector((state: RootState) => state.doctorNoOrg.doctor);
    const status = useAppSelector((state: RootState) => state.doctorNoOrg.status);
    const error = useAppSelector((state: RootState) => state.doctorNoOrg.error);
    const countries = useAppSelector((state: RootState) => state.country.countries);


    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemoveDoctorWarningVisible, setIsRemoveDoctorWarningVisible] = useState(false);
    const [isDeleteDoctorWarningVisible, setIsDeleteDoctorWarningVisible] = useState(false);

    const doctorId = params.doctorId;

    useEffect(() => {
        if (doctorsStatus === "idle") {
            dispatch(fetchNoOrgDoctors({}));
        }
        dispatch(fetchNoOrgDoctor({doctorId: doctorId!}));
    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchNoOrgDoctors({}));
            dispatch(initNoOrgDoctorState());
            navigate(-1);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(-1);
    }

    const changeDoctor = (doctor: DoctorRequestDto) => {
        dispatch(updateNoOrgDoctorFields(doctor));
    }

    const onSave = () => {
        dispatch(saveNoOrgDoctor({doctorId: doctorId!, doctor: doctor as DoctorRequestDto}));
    }


    const onDelete = () => {
        setIsDeleteDoctorWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemoveDoctorWarningVisible(true);
    }

    const onDeleteDoctor = () => {
        if (doctor?.id != null) {
            dispatch(deleteNoOrgDoctor({doctorId: doctor.id}));
        }
    }

    const onConfirm = (phoneNumber: string) => {
        dispatch(updateNoOrgDoctorPhoneNumber(phoneNumber));
    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSaveNoOrgDoctorError());
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }


    if (status === "idle" || status === "loading" || doctorsStatus === "idle") {
        return <Spin/>;
    }

    if (status === "auth-error") {
        return <Modal title={t("error.auth")} visible={isModalVisible} onOk={handleAuthOk} onCancel={handleCancel}>
            <p>{t("error.authFailedLogin")}</p>
        </Modal>
    }

    if (status === "error") {
        const apiError = error as Problem;
        const title = apiError.title ?? t("error.server");
        const detail = apiError.detail ?? error?.toString;
        return <Modal title={title} visible={isModalVisible} onOk={handleErrorOk} onCancel={handleCancel}>
            <p>{detail}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }

    return (<div className='edit-person-main-frame'>
        <Col span={24}>
            <Form className='person-form'
                  name="basic"
                  labelCol={{span: 6}}
                  wrapperCol={{span: 24}}
                  initialValues={{remember: true}}
                //onFinish={onFinish}
                //onFinishFailed={onFinishFailed}
                  autoComplete="off"
            >

                <Row>
                    {/* <Col className='person-title' offset={6} span={11}>Doctor</Col> */}
                    <Col offset={6} span={18}>
                        <Form.Item>
                            <Row justify="end">
                                <Space className="mr-xxs">
                                    <Button icon={<DeleteOutlined/>} onClick={onDelete} type='primary' danger
                                            disabled={doctor?.id === undefined}>{t("actions.delete")}</Button>
                                </Space>

                                <Space>
                                    <Button icon={<CloseOutlined/>} onClick={onCancel}>{t("actions.cancel")}</Button>
                                    <Button icon={<CheckOutlined/>} type='primary' onClick={onSave}>{t("actions.save")}</Button>
                                </Space>
                            </Row>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.phoneNumber")} rules={[{required: true}]}
                                   initialValue={doctor?.userAccount?.mobileNumber}>
                            <Input disabled placeholder={t("placeholder.phoneNumber")}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.email")}>
                            <Input placeholder={t("placeholder.email")}
                                   defaultValue={doctor?.userAccount?.emailAddress || ""} onChange={(e) => {
                                changeDoctor({...doctor, email: e.target.value} as DoctorRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.firstName")} rules={[{required: true, message: t("validation.firstNameRequired")}]}>
                            <Input placeholder={t("placeholder.firstName")} defaultValue={doctor?.firstName || ""} onChange={(e) => {
                                changeDoctor({...doctor, firstName: e.target.value} as DoctorRequestDto)
                            }}/>
                        </Form.Item>

                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.lastName")}>
                            <Input placeholder={t("placeholder.lastName")} defaultValue={doctor?.lastName || ""} onChange={(e) => {
                                changeDoctor({...doctor, lastName: e.target.value} as DoctorRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.country")} initialValue={doctor?.address?.country}>
                            <Select
                                onChange={(e) => changeDoctor({
                                    ...doctor,
                                    address: {...doctor?.address, alpha3Code: e}
                                } as DoctorRequestDto)}
                                defaultValue={doctor?.address?.alpha3Code}
                                showSearch
                                style={{width: 200}}
                                placeholder={t("placeholder.searchToSelect")}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }}
                                filterSort={(optionA, optionB) =>
                                    (optionA!.children![2] as unknown as string)
                                        .toLowerCase()
                                        .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                                }
                            >
                                {countries.map(country => <Option key={country.alpha3Code}
                                                                  value={country.alpha3Code}><img src={country.flag!}
                                                                                                  width="20"></img> {country.name}
                                </Option>)}

                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={doctor?.address?.streetAddress1 as string | undefined}
                                   placeholder={t("placeholder.addressLine1")} onChange={(e) => {
                                changeDoctor({
                                    ...doctor,
                                    address: {...doctor?.address, streetAddress1: e.target.value}
                                } as DoctorRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={doctor?.address?.streetAddress2 as string | undefined}
                                   placeholder={t("placeholder.addressLine2")} onChange={(e) => {
                                changeDoctor({
                                    ...doctor,
                                    address: {...doctor?.address, streetAddress2: e.target.value}
                                } as DoctorRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.city")}>
                            <Input defaultValue={doctor?.address?.city as string | undefined} placeholder={t("placeholder.city")}
                                   onChange={(e) => {
                                       changeDoctor({
                                           ...doctor,
                                           address: {...doctor?.address, city: e.target.value}
                                       } as DoctorRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.state")}>
                            <Input defaultValue={doctor?.address?.state as string | undefined} placeholder={t("placeholder.state")}
                                   onChange={(e) => {
                                       changeDoctor({
                                           ...doctor,
                                           address: {...doctor?.address, state: e.target.value}
                                       } as DoctorRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.postcode")}>
                            <Input defaultValue={doctor?.address?.postCode as string | undefined} placeholder={t("placeholder.postcode")}
                                   onChange={(e) => {
                                       changeDoctor({
                                           ...doctor,
                                           address: {...doctor?.address, postCode: e.target.value}
                                       } as DoctorRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Col>
        <Modal visible={isDeleteDoctorWarningVisible} okText={t("actions.yes")} cancelText={t("actions.no")}
               onCancel={() => setIsDeleteDoctorWarningVisible(false)} onOk={onDeleteDoctor}>{t("confirm.delete.doctor", { name: [doctor?.firstName, doctor?.lastName].filter(Boolean).join(" ") })}</Modal>
    </div>);
}